import { AfterViewInit, Component, OnDestroy, OnInit, inject } from "@angular/core";
import { ApicallOption } from "@core-services/apicall.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { currentDateWithoutTime } from "@helpers/date";
import { Helper } from "@helpers/functions";
import { ProviderType } from "@helpers/types";
import { PayAccountService } from "@main-services/pay_account.service";
import { SocketService } from "@main-services/socket.service";
import { Fiat, FiatWallet, Field, Limit, PayoutAccount, PayoutProvider, Provider } from "@models";
import { BpModalHasSave } from "@models/interfaces/bp-modal-has-save";
import { FiatSendWrapperData } from "@models/interfaces/fiat-send-wrapper-data";
import { ModalHeader } from "@models/modal_step";
import { PasswordAuth } from "@models/password_auth";
import { FiatTransaction } from "@models/transaction";
import { VerifyPayeeMode, VerifyPayeeResult } from "@models/verify-payee";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { WalletBalance } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance.model";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { FiatPrecisionSymbolPipe } from "@modules/core/pipes";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { FiatDepositModalComponent } from "@modules/main/components/step-summary/fiat-transactions/fiat-deposit-modal/fiat-deposit-modal.component";
import { WithdrawSuccessComponent } from "@modules/main/components/step-summary/success-screens/withdraw-success/withdraw-success.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EMPTY, Observable, Subject, of as observableOf } from "rxjs";
import { filter, finalize, first, map, switchMap, take, tap } from "rxjs/operators";
import { FiatWithdrawalFraudComponent } from "../../success-screens/fiat-withdraw-fraud/fiat-withdrawal-fraud.component";
import { WithdrawPendingComponent } from "../../success-screens/withdraw-pending/withdraw-pending.component";
import { FiatTransactionModal } from "../fiat-transaction.modal";

@UntilDestroy()
@Component({
	templateUrl: "./fiat-withdraw-modal.component.html",
	styleUrls: ["./fiat-withdraw-modal.component.scss"],
	standalone: false
})
export class FiatWithdrawalModalComponent
	extends FiatTransactionModal
	implements AfterViewInit, BpModalHasSave, OnDestroy, OnInit
{
	private cashPlusPortfolioFacade = inject(CashplusPortfolioFacade);

	public editAccount: PayoutAccount = null;
	public providerStack: PayoutProvider;

	public header_key: string = "withdraw_header_modal";
	public how_much_money_key: string = "how_much_money_withdraw";
	public min_amount_withdrawal_key: string = "min_amount_withdraw_AMOUNT";
	public max_amount_withdrawal_key: string = "FIRST_is_higher_than_your_limit_SECOND";
	public addUnavailable: boolean = false;
	public sendEnabled: boolean = false;
	public preselected: FiatWallet = null;
	public amountInvalidMax: boolean = false;
	public amountInvalidMin: boolean = false;
	public balance: WalletBalance = null;
	public showPaymentReference: boolean = false;
	public showPassword: boolean = false;
	public showSmsPin: boolean = false;
	public isReferenceValid: boolean = false;
	public referenceRegex: RegExp = new RegExp("^[a-zA-Z0-9 .,:+-/()?]{0,35}$");
	public pageChangeSubject: Subject<void> = new Subject();
	public data: FiatSendWrapperData = null;

	// cash plus
	public checkIfParkAssetIsAvailable$: Observable<boolean>;

	protected label: string = "withdrawal";
	protected readonly flowName: string = "Withdraw";
	protected readonly pathPrepend: string = "/withdrawal";
	private changeNameFlag: boolean = false;
	private canDismiss: boolean = false;
	public constructor(
		protected socketService: SocketService,
		protected providerService: PayAccountService,
		protected confirmModalService: ConfirmModalService,
		protected readonly walletsBalancesFacade: WalletsBalancesFacadeService,
		protected readonly fiatPrecisionSymbolPipe: FiatPrecisionSymbolPipe,
		protected profileFacade: NewProfileFacade
	) {
		super(confirmModalService);
	}

	public ngOnInit(): void {
		this.verifyPayeeService.verifyPayeeMode.set(VerifyPayeeMode.WITHDRAWAL);
		this.modalService.modalDismissed$
			.pipe(
				take(1),
				switchMap(() => {
					if (this.verifyPayeeService.verifyPayeeResponse()?.id) {
						return this.verifyPayeeService.updateAcceptanceStatus().pipe(take(1));
					}
					return EMPTY;
				})
			)
			.subscribe();
	}

	public get isFirstStep(): boolean {
		return (this.wallet == null && this.isEdit(0)) || this.isEdit(1);
	}

	public get isSecondStep(): boolean {
		return (this.wallet != null && this.account == null && this.isEdit(0)) || this.isEdit(2);
	}

	public get isThirdStep(): boolean {
		return (this.wallet != null && this.account != null && this.isEdit(0)) || this.isEdit(3);
	}

	public get wallets(): FiatWallet[] {
		return this.fiatwalletService.list.filter((w) => w.getBalance() > 0);
	}

	public get accounts(): PayoutAccount[] {
		return this.providerService.list
			.filter((p: PayoutAccount) => !p.isDeleted())
			.filter((p: PayoutAccount) => p.getFiatId() === this.wallet.getFiatId());
	}

	public get fiat(): Fiat {
		return this.fiatService.getItemById(this.wallet.getFiatId());
	}

	public get wallet(): FiatWallet {
		return this.fiatwalletService.getItemById(this.data.transaction.getFiatWalletId());
	}

	public set wallet(w: FiatWallet) {
		this.data.transaction.setFiatWalletId(w.getId());
		this.data.transaction.setFiatId(this.fiat.getId());
		this.walletsBalancesFacade
			.getFiatWalletBalance$(w.getId())
			.pipe(untilDestroyed(this))
			.subscribe((balance: WalletBalance) => (this.balance = balance));
	}

	public get account(): PayoutAccount {
		return this.providerService.getItemById(this.data.transaction.getPayoutAccountId());
	}

	public set account(p: PayoutAccount) {
		this.data.transaction.setPayoutAccountId(p.getId());
	}

	public get provider(): PayoutProvider {
		return this.masterdataService.getPayoutProviderById(this.account.getPayoutProviderId());
	}

	public get maxAmount(): number {
		if (this.limit.getMax() > 0) {
			return Math.min(this.limit.getMax(), this.balance.operationalBalance);
		}
		return this.balance.operationalBalance;
	}

	public getAccountProviders(): ProviderType[] {
		const items: ProviderType[] = [];
		const fiat = this.fiat;

		this.masterdataService.payout_providers
			.filter((p: PayoutProvider) => p.isEnabled())
			.filter((p: PayoutProvider) => p.sendEnabled(false))
			.filter((p: PayoutProvider) => p.hasFiat(fiat))
			.forEach((p: PayoutProvider) => {
				const account = this.accountsForProvider(p);
				if (account.length === 1) {
					items.push(account[0]);
				} else if (p.hasFiat(this.fiat)) {
					items.push(p);
				}
			});
		return items;
	}

	public getProvidersNotForFiat(): PayoutProvider[] {
		const fiat = this.fiat;
		return this.masterdataService.payout_providers
			.filter((p: PayoutProvider) => p.isEnabled())
			.filter((p: PayoutProvider) => p.sendEnabled(false))
			.filter((p: PayoutProvider) => !p.hasFiat(fiat))
			.filter((p: PayoutProvider) => p.getFees().length > 0);
	}

	public get isPasswordVisible(): boolean {
		return this.showPassword;
	}

	public get isReferenceVisible(): boolean {
		return this.showPaymentReference;
	}

	public get isSmsPinVisible(): boolean {
		return this.showSmsPin;
	}

	public get currentStep(): ModalHeader {
		const header: ModalHeader = this.getDefaultHeader(this.translate(this.header_key));

		if ((this.isFirstStep && (this.isEdit(0) || this.isEdit(1))) || (this.isSecondStep && this.isEdit(2))) {
			header.left = "abort";
		} else if (
			(this.isSecondStep && (!(this.isEdit(2) && this.sendEnabled) || this.isReferenceVisible)) ||
			(this.isThirdStep && !this.isEdit(3))
		) {
			header.left = "back";
		}
		if (
			(this.isFirstStep && this.isEdit(1)) ||
			(this.isFirstStep && this.isEdit(0) && !!this.preselected) ||
			(this.isSecondStep && this.isEdit(2))
		) {
			header.right = "ok";
		} else if (
			(this.isFirstStep && (!this.isEdit(0) || !this.isEdit(1))) ||
			(this.isSecondStep && !this.isEdit(2)) ||
			this.isThirdStep
		) {
			header.right = "abort";
		}

		return header;
	}

	public ngAfterViewInit(): void {
		if (this.data.transaction.getFiatWalletId() !== "") {
			this.selectWallet(this.fiatwalletService.getItemById(this.data.transaction.getFiatWalletId()));
		}

		this.initTracking();

		super.ngAfterViewInit();

		// assign the check here due to the fact that the data is not available in the constructor
		this.checkIfParkAssetIsAvailable$ = this.cashPlusPortfolioFacade
			.getPortfolioPerFiatState$(this.fiat.getPid())
			.pipe(
				map((val) => {
					if (val) {
						return +val.parked_asset_balance > 0;
					}

					return false;
				})
			);
	}

	public ngOnDestroy(): void {
		this.pageChangeSubject.complete();
	}

	public save(): void {
		if (!this.summary) {
			return;
		}
		const shouldSendVerificationId =
			this.verifyPayeeService.isVopFlagEnabled() && Boolean(this.verifyPayeeService.verifyPayeeResponse()?.id);
		this.apicallService
			.post(
				"fiatwallet/" + this.wallet.getId() + "/withdraw",
				new ApicallOption(FiatTransaction, {
					amount: this.data.transaction.getAmount(),
					auth_code: this.data.transaction._auth_code,
					is_fee_excl: !this.data.transaction.areFeesIncluded,
					payout_account_id: this.data.transaction.getPayoutAccountId(),
					...(shouldSendVerificationId && {
						verification_id: this.verifyPayeeService.verifyPayeeResponse().id
					})
				})
			)
			.pipe(map((res) => res[0]))
			.subscribe(
				(transaction: FiatTransaction) => {
					this.fiatwalletService.initWallets(true);
					this.fiatwalletService.transactionReceived();
					this.saved = true;
					this.account.wasUsed();
					this.showSuccessScreen(transaction);
					super.done();
				},
				(error) => Helper.handleError(error)
			);
	}

	public showSuccessScreen(transaction: FiatTransaction): void {
		const waitingModal = this.modalService.create(WithdrawPendingComponent, { transaction }).present();
		this.socketService
			.joinRoom<FiatTransaction>({
				name: "fiat_wallet_transactions",
				isPrivate: true,
				type: FiatTransaction
			})
			.pipe(
				filter((t: FiatTransaction) => t.getId() === transaction.getId()),
				filter((t: FiatTransaction) => t.isConfirmed()),
				first()
			)
			.subscribe((trans: FiatTransaction) => {
				waitingModal.dismiss();
				const modalComponent = trans.isWithdrawalFraud()
					? FiatWithdrawalFraudComponent
					: WithdrawSuccessComponent;
				const modalComponentInputs = trans.isWithdrawalFraud()
					? { fiatTransaction: trans }
					: { fiatName: this.fiat.getName() };
				this.modalService
					.create<
						FiatWithdrawalFraudComponent | WithdrawSuccessComponent
					>(modalComponent, modalComponentInputs)
					.present();
			});
	}

	public goToSummary(): void {
		if (this.goToSummaryAllowed()) {
			this.moveToSummary();
		}
	}

	public goToFiatWallet(): void {
		this.close();
		this.router.navigateByUrl(`/fiat-wallet/${this.wallet.getId()}`);
	}

	public goToSummaryAllowed(): boolean {
		return this.validateMax() && this.validateMin();
	}

	public selectWallet(fw: FiatWallet): void {
		if (this.wallet == null || this.data.transaction.getDirectFiatTransaction()) {
			if (this.wallet != null) {
				this.data.transaction.setDirectFiatTransaction(false);
			}
			this.wallet = fw;
			this.preselected = fw;
			this.pageChangeSubject.next();
			return;
		}
		this.edit = 0;

		if (this.wallet.getId() === fw.getId() && !this.data.transaction.getDirectFiatTransaction()) {
			this.summary = true;
			this.pageChangeSubject.next();
			return;
		}
		this.wallet = fw;
		this.account = new PayoutAccount();
		this.editAccount = null;
		this.providerStack = null;
		this.pageChangeSubject.next();
	}

	public setEdit(step: number, b = false): void {
		if (step === 2) {
			this.editAccount = null;
			this.providerStack = null;
		}

		super.setEdit(step, b);
		this.pageChangeSubject.next();
	}

	public setAmount(amount: number): void {
		this.data.transaction.setAmount(+amount);
		this.userWants();
		this.goToSummary();
		this.pageChangeSubject.next();
	}

	public stepBack(step: number): void {
		// User comes from summary, kill all old edit/stacks
		if (this.editAccount != null) {
			this.editAccount = null;
			this.doTrapFocusTime();
			this.pageChangeSubject.next();
			return;
		}

		if (this.providerStack != null) {
			this.providerStack = null;
			this.doTrapFocusTime();
			this.pageChangeSubject.next();
			return;
		}

		this.account = new PayoutAccount();
		if (step === 1) {
			this.wallet = new FiatWallet();
		}
		this.edit = 0;
		this.doTrapFocusTime();
		this.pageChangeSubject.next();
	}

	public selectAccount(account: PayoutAccount): void {
		this.isBankTransfer.set(account.getPayoutProviderId() === "1");
		this.selectedAccount.set(account);
		this.editAccount = null;
		const result: VerifyPayeeResult = this.verifyPayeeService.verifyPayeeResponse()?.result;
		const shouldHandleVop =
			this.verifyPayeeService.isVopFlagEnabled() &&
			(result === VerifyPayeeResult.NO_MATCH || result === VerifyPayeeResult.CLOSE_MATCH);
		if (this.edit > 0 && !shouldHandleVop) {
			if (this.account != null && this.account.getId() === account.getId()) {
				this.summary = true;
				this.doTrapFocusTime();
				this.pageChangeSubject.next();
				return;
			}
		}

		// Check if the account has a BIC before going further
		const bicField: Field = account.getFields().find((field: Field) => field.getName() === "bic");
		const provider = this.masterdataService.getPayoutProviderById(account.getPayoutProviderId());
		if (this.isAccount(account) && provider.isShortNameSepa() && (!bicField || !bicField.getValue())) {
			this.infoService.setError(this.translationService.translate("withdraw_money_bic_swift_not_set"));
			return;
		}

		this.apicallService
			.post(
				"user/limits",
				new ApicallOption(Limit, {
					type: "withdrawal",
					payout_provider_id: account.getProviderId(),
					fiat_id: this.fiat.getId(),
					payout_account_id: account.getId()
				})
			)
			.pipe(tap(() => this.walletsBalancesFacade.refreshBalances$().toPromise()))
			.subscribe(
				(a) => {
					this.data.transaction.setFee(0);
					this.data.transaction["amount"] = "";
					this.account = account;
					this.edit = 0;
					this.limit = a[0];
					this.doTrapFocusTime();
					this.pageChangeSubject.next();
				},
				(error) => {
					Helper.handleError(error);
				}
			);
	}

	public selectEditAccount(account: PayoutAccount): void {
		this.editAccount = new PayoutAccount();
		this.editAccount = Object.assign(this.editAccount, account);
		this.editAccount.setFields(Object.assign([], account.getFields()));
		this.animate();
		this.pageChangeSubject.next();
		this.doTrapFocusTime();
	}

	public isAddAllowed(p: ProviderType): boolean {
		let length;
		let max;
		if (p instanceof PayoutAccount) {
			const provider = this.masterdataService.getPayoutProviderById(p.getPayoutProviderId());
			const accountFee = provider.getFeeByFiatId(p.getFiatId());
			length = this.accountsForProvider(provider).length;
			max = accountFee.getAllowedPayoutAccounts();
			return length < max;
		}

		length = this.accountsForProvider(p).length;
		const providerFee = p.getFeeByFiatId(this.fiat.getId());
		max = providerFee.getAllowedPayoutAccounts();
		return length < max && p.account_creation_enabled;
	}

	public canAddAccount(account: PayoutAccount): boolean {
		return this.isAddAllowed(account) && !this.sendEnabled;
	}

	public canAddPaymentAccount(account: ProviderType): boolean {
		if (!this.isAccount(account)) {
			return false;
		}
		return this.sendEnabled && this.isAddAllowed(account);
	}

	public clickedProvider(p: ProviderType): void {
		this.isBankTransfer.set(p.getId() === "1");
		if (this.isStacked(p)) {
			this.providerStack = <PayoutProvider>p;
			this.doTrapFocusTime();
		} else {
			if (!this.isAddAllowed(p)) {
				this.addUnavailable = true;
				return;
			}

			this.createAccount(p);
		}
	}

	public createAccount(p: ProviderType): void {
		if (p instanceof PayoutAccount) {
			p = this.masterdataService.getPayoutProviderById(p.getPayoutProviderId());
		}

		// If user is verified we should display his name in a locked field.
		if ((this.user.isVerified() || this.user.isDocumented()) && !this.sendEnabled) {
			const nameField = p.getFields().find((f) => f.getName() === "payout_receiver_name");
			if (nameField) {
				this.profileFacade
					.getProfile$()
					.pipe(untilDestroyed(this))
					.subscribe((profile) => {
						nameField.setValue(profile.fullName);
					});
			}
		}

		this.editAccount = new PayoutAccount();
		this.editAccount.setProviderId(p.getId());
		this.editAccount.setFiatId(this.fiat.getId());
		this.editAccount.setFields(p.getFields());
		this.animate();
		this.doTrapFocusTime();
	}

	public deleteAccount(p: ProviderType): void {
		if (this.isAccount(p)) {
			this.confirmModalService
				.showConfirmModal(
					"delete_payout_account_title",
					"do_you_really_want_to_delete_payout_account",
					"ok",
					"cancel"
				)
				.subscribe((a) => {
					if (a) {
						this.providerService.deletePayoutAccount(p as PayoutAccount).subscribe();
					}
				});
		}
	}

	public createdAccount(account: PayoutAccount): void {
		this.selectAccount(account);
	}

	public showProviderList(): boolean {
		return !this.editAccount && !this.providerStack && !this.addUnavailable;
	}

	public isAccount(a: ProviderType): boolean {
		return a instanceof PayoutAccount;
	}

	public stacks(p: ProviderType): number {
		if (this.isAccount(p)) {
			return 1;
		}
		return this.accountsForProvider(<PayoutProvider>p).length;
	}

	public isStacked(p: ProviderType): boolean {
		return this.stacks(p) > 1;
	}

	public isWalletActive(fw: FiatWallet): boolean {
		return (
			(this.isEdit(1) && this.isSame(fw, this.wallet)) ||
			(this.isSame(fw, this.preselected) && this.wallet == null)
		);
	}

	public accountsForProvider(p: PayoutProvider): PayoutAccount[] {
		return this.accounts.filter((a: PayoutAccount) => a.getProviderId() === p.getId());
	}

	public accountsForProviderObs(p: PayoutProvider): Observable<PayoutAccount[]> {
		return observableOf(this.accountsForProvider(p));
	}

	public userWants(): void {
		// UserWants and checkMax now set frontend-side calculated fee ONLY in the fiat-withdraw flow. In fiat-deposit, this was moved to backend
		this.data.transaction.setFee(this.getFee(this.amount));
		this.data.transaction.setFiatId(this.fiat.getId());
		if (this.amount < this.limit.getMin()) {
			this.data.transaction.setAmount(Math.max(0, this.data.transaction.getAmount()));
			this.data.transaction.setFee(this.getFee(this.amount));
			return;
		}
		this.isMax();
	}

	public isMax(): boolean {
		if (this.amount > this.maxAmount && this.maxAmount > 0) {
			this.data.transaction.setAmount(Math.max(0, this.data.transaction.getAmount()));
			this.data.transaction.setFee(this.getFee(this.amount));

			return false;
		}

		return true;
	}

	public isSamePayout(a: PayoutProvider, account: PayoutAccount): boolean {
		if (a instanceof PayoutAccount) {
			return super.isSame(a, account);
		}
		return this.accountsForProvider(a).some((ac: PayoutAccount) => super.isSame(ac, account));
	}

	public cancelDetail(): void {
		if (this.editAccount.isUsed()) {
			this.confirmModalService
				.showConfirmModal(
					"delete_payout_account_title",
					"do_you_really_want_to_delete_payout_account",
					"ok",
					"cancel"
				)
				.subscribe((a: boolean) => {
					if (a) {
						this.providerService
							.deletePayoutAccount(this.editAccount)
							.subscribe(() => (this.editAccount = null));
					}
				});
		} else {
			this.editAccount = null;
		}
	}

	public modalCanDismiss(): boolean | Observable<boolean> {
		if (this.dirty && !this.saved && !this.changeNameFlag && !this.canDismiss) {
			return this.confirmModalService.showConfirmModal("close_window?", "unsaved_data_will_get_lost").pipe(
				tap((a) => {
					if (a) {
						this.trackCancel();
					}
				})
			);
		}
		return true;
	}

	public onChangeName(): void {
		this.confirmModalService
			.showConfirmModal(
				"fiat_withdraw_bank_transfer_account_change_name_popup_title",
				"fiat_withdraw_bank_transfer_account_change_name_popup_description",
				"ok",
				"cancel"
			)
			.subscribe((yes) => {
				if (yes) {
					this.changeNameFlag = true;
					this.closeAll();
					setTimeout(() => {
						// The Backbutten handling in Modals screws this if you dont delay it a little
						this.router.navigateByUrl("/account");
					}, 300);
				}
			});
	}

	// Payments related:
	public onPasswordSuccess($event: PasswordAuth): void {
		// Empty
	}

	public onSavePaymentReference(): void {
		// Empty
	}

	public getCurrentDate(): string {
		return currentDateWithoutTime();
	}

	public getSendFiatWallets(): Observable<FiatWallet[]> {
		return this.fiatwalletService
			.getList()
			.pipe(
				map((list: FiatWallet[]) =>
					list.filter((w: FiatWallet) => w.getBalance() > 0 && this.isWalletSendEnabled(w.getCurrencyId()))
				)
			);
	}

	public getFiatById(fiatId: string): Fiat {
		return this.fiatService.getItemById(fiatId);
	}

	public onPaymentReferenceChange($event): void {
		this.isReferenceValid = this.referenceRegex.test(this.data.transaction.getPaymentReference());
	}

	public onPayoutAccountPasswordError(): void {
		// Logic not needed in withdraw -> overridden in Send
	}

	public validReference(): boolean {
		return (
			this.data.transaction.getPaymentReference() &&
			this.data.transaction.getPaymentReference().length >= 3 &&
			this.isReferenceValid
		);
	}

	public onHeaderButtonClick(name: string): void {
		if (name === "back") {
			if (this.isSecondStep) {
				if (this.addUnavailable) {
					this.addUnavailable = false;
				} else {
					this.stepBack(1);
				}
			} else {
				this.data.transaction.stepBack();
			}
		} else if (name === "abort") {
			this.close();
		} else if (name === "ok" && this.isFirstStep) {
			const wallet: FiatWallet = this.preselected;
			this.selectWallet(wallet);
		} else {
			super.onHeaderButtonClick(name);
		}
	}

	public initTracking(): void {
		this.pageChangeSubject
			.pipe(finalize(() => this.rudderstackFlowState(this.saved ? "successful" : "cancelled")))
			.subscribe(() => {
				if (this.summary) {
					this.rudderstackPageFlow("Summary", "summary", {
						withdrawal_method: this.provider.getShortName(),
						withdrawal_amount: "" + this.amount
					});
				} else if (this.isThirdStep) {
					this.rudderstackPageFlow("Amount", "amount", {
						withdrawal_method: this.provider.getShortName()
					});
				} else if (this.isSecondStep) {
					if (this.editAccount) {
						const provider = this.masterdataService.getPayoutProviderById(
							this.editAccount.getPayoutProviderId()
						);
						this.rudderstackPageFlow("Bank Details", "method", {
							withdrawal_method: provider.getShortName()
						});
					} else {
						this.rudderstackPageFlow("Method Selection", "method");
					}
				} else if (this.isFirstStep) {
					this.rudderstackPageFlow("Currency Selection");
				}
			});

		this.rudderstackFlowState("started");

		this.pageChangeSubject.next();
	}

	public logoUrl(p: PayoutAccount | Provider): string {
		return this.masterdataService.logoUrl(p);
	}

	public switchToDeposit(): void {
		this.canDismiss = true;
		const data = new FiatTransaction();
		data.setFiatWalletId(this.wallet.getId());
		const modal = this.modalService.create(FiatDepositModalComponent, { data: { transaction: data } });
		this.close();
		modal.present();
	}

	public verifyPayeeAction(): void {
		const result: VerifyPayeeResult = this.verifyPayeeService.verifyPayeeResponse().result;
		if (result === VerifyPayeeResult.ERROR) {
			this.goToSummary();
		}
		if (result === VerifyPayeeResult.NO_MATCH || result === VerifyPayeeResult.CLOSE_MATCH) {
			this.setEdit(2);
		}
	}

	private validateMax(): boolean {
		this.amountInvalidMax = this.amountExceedsMax();
		const total = this.fiatPrecisionSymbolPipe.transform(this.data.transaction.total, {
			id: this.fiat?.getId(),
			currencies: [this.fiat]
		});
		if (this.amountInvalidMax) {
			this.showToastr(this.max_amount_withdrawal_key, [total + "", this.maxAmount + ""]);
		}

		return !this.amountInvalidMax;
	}

	private validateMin(): boolean {
		this.amountInvalidMin = !this.isSufficient();
		if (this.amountInvalidMin) {
			this.showToastr(this.min_amount_withdrawal_key, [
				this.minTransactionAmount() + " " + this.fiat.getSymbol()
			]);
		}
		return !this.amountInvalidMin;
	}

	private showToastr(key, translateParams: string[]): void {
		const msg = this.translationService.translate(key, translateParams);
		this.infoService.setError(msg);
	}

	private amountExceedsMax(): boolean {
		return this.data.transaction.total > this.maxAmount && this.maxAmount > 0;
	}

	private animate(): void {
		// $(".modal-tile").animate({ scrollTop: 0 }); // jquery deprecated
		//  Can be replaced by angular animation
	}

	private isWalletSendEnabled(fiatId: string): boolean {
		const fiat = this.getFiatById(fiatId);
		return !!this.masterdataService.payout_providers.find(
			(p: PayoutProvider) => p.isEnabled() && p.sendEnabled(true) && p.hasFiat(fiat)
		);
	}
}
